import { useState, useEffect, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import useAppRoot from '../hooks/useAppRoot';
import axios from 'axios';
import './TracingList.scss';

const QuotationsTable = ({ quotations, paymentDate = false, emptyMessage = 'No tiene cotizaciones' }) => {
    const appRoot = useAppRoot();

    const scrollRef = useRef(null);
    const [hideHint, setHideHint] = useState(false);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const handleScroll = () => {
            setHideHint(true);
        };

        container.addEventListener('touchend', handleScroll);
        return () => container.removeEventListener('touchend', handleScroll);
    }, [hideHint]);

    const priceBodyTemplate = (quotation) => {
        return quotation.precio.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
    };

    const statusBodyTemplate = (quotation) => {
        return <span className={classNames('badge', quotation.estado.toLowerCase())}>{quotation.estado}</span>;
    };

    const actionsBodyTemplate = (quotation) => {
        return (
            <Button
                className="text-nowrap"
                label="Ver más"
                onClick={() => {
                    window.location = appRoot + '/seguimiento/' + quotation.id_registro;
                }}
            />
        );
    };

    return (
        <>
            <div className="responsive-table-wrapper">
                <div className="table-overlay-container">
                    {!hideHint && <div className="scroll-overlay">Desliza hacia la derecha →</div>}
                    <div className="scroll-container" ref={scrollRef}>
                        <DataTable value={quotations} paginator rows={20} sortField="creacion" sortOrder={-1} emptyMessage={emptyMessage}>
                            <Column field="id_registro" header="#" sortable className="fw-bold" />
                            <Column field="nombre" header="Nombre" sortable />
                            <Column field="telefono" header="Teléfono" sortable />
                            <Column field="creacion" header="Fecha Creación" sortable />
                            <Column field="precio" header="Precio" sortable body={priceBodyTemplate} className="fw-bold" />
                            <Column field="estado" header="Estado" body={statusBodyTemplate} />
                            {paymentDate && <Column field="fecha_pago" header="Fecha Pago" sortable />}
                            <Column header="Acciones" body={actionsBodyTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>
        </>
    );
};

const TracingList = ({ search }) => {
    const [openQuotations, setOpenQuotations] = useState([]);
    const [quotedQuotations, setQuotedQuotations] = useState([]);
    const [closedQuotations, setClosedQuotations] = useState([]);

    const [loadingOpen, setLoadingOpen] = useState(false);
    const [loadingQuoted, setLoadingQuoted] = useState(false);
    const [loadingClosed, setLoadingClosed] = useState(false);

    const [errorLoadingOpen, setErrorLoadingOpen] = useState(false);
    const [errorLoadingQuoted, setErrorLoadingQuoted] = useState(false);
    const [errorLoadingClosed, setErrorLoadingClosed] = useState(false);

    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setLoadingOpen(true);
        axios
            .get('/seguimiento/busqueda?estado=ingresado&paciente=' + (search ? search : ''))
            .then((response) => {
                setOpenQuotations(response.data);
                if (response.data.length == 0) {
                    setActiveIndex(1);
                } else {
                    setActiveIndex(0);
                }
            })
            .catch(() => {
                setErrorLoadingOpen(true);
            })
            .finally(() => {
                setLoadingOpen(false);
            });

        setLoadingQuoted(true);
        axios
            .get('/seguimiento/busqueda?estado=Cotizado&paciente=' + (search ? search : ''))
            .then((response) => {
                setQuotedQuotations(response.data);
            })
            .catch(() => {
                setErrorLoadingQuoted(true);
            })
            .finally(() => {
                setLoadingQuoted(false);
            });

        setLoadingClosed(true);
        axios
            .get('/seguimiento/busqueda?estado=pagado&paciente=' + (search ? search : ''))
            .then((response) => {
                setClosedQuotations(response.data);
            })
            .catch(() => {
                setErrorLoadingClosed(true);
            })
            .finally(() => {
                setLoadingClosed(false);
            });
    }, []);

    return (
        <>
            <TabView className="full-width" renderActiveOnly={false} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Ingresados">
                    <QuotationsTable
                        quotations={openQuotations}
                        emptyMessage={
                            !errorLoadingOpen
                                ? loadingOpen
                                    ? 'Cargando...'
                                    : 'No tiene cotizaciones ingresadas. Verifique en la pestaña Cotizados o Pagados'
                                : 'Error al cargar. Recargue la página.'
                        }
                    />
                </TabPanel>
                <TabPanel header="Cotizados">
                    <QuotationsTable
                        quotations={quotedQuotations}
                        emptyMessage={
                            !errorLoadingQuoted
                                ? loadingQuoted
                                    ? 'Cargando...'
                                    : 'No tiene cotizaciones cotizadas. Verifique en la pestaña Ingresados o Pagados'
                                : 'Error al cargar. Recargue la página.'
                        }
                    />
                </TabPanel>
                <TabPanel header="Pagados">
                    <QuotationsTable
                        quotations={closedQuotations}
                        paymentDate
                        emptyMessage={
                            !errorLoadingClosed
                                ? loadingClosed
                                    ? 'Cargando...'
                                    : 'No tiene cotizaciones pagadas. Verifique en la pestaña Ingresados o Cotizados'
                                : 'Error al cargar. Recargue la página.'
                        }
                    />
                </TabPanel>
            </TabView>
        </>
    );
};

export default TracingList;
